import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, MapPin, CheckCircle2 } from 'lucide-react';
import { useLocation } from '@/shared/hooks/useLocation';
import {
  actionsApi,
  type ActionItem,
  type ActionPriority,
  type ActionFocusArea,
} from '../api/actionsApi';
import { dashboardKeys } from '../queryKeys';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { PageHeader } from '@/shared/ui';
import styles from './ActionQueuePage.module.css';

// ─── Config ───────────────────────────────────────────────────────────────────

const PRIORITY_LABELS: Record<ActionPriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const PRIORITY_COLORS: Record<ActionPriority, string> = {
  critical: 'var(--error)',
  high: 'var(--warning-hover)',
  medium: 'var(--warning)',
  low: 'var(--info)',
};

const FOCUS_AREA_LABELS: Record<ActionFocusArea, string> = {
  technical: 'Technical SEO',
  on_page: 'On-Page',
  local: 'Local SEO',
  off_page: 'Off-Page',
  schema: 'Schema',
  aeo: 'AEO / AI',
  conversion: 'Conversion',
  competitive: 'Competitive',
};

const EFFORT_LABELS: Record<string, string> = {
  low: 'Quick',
  medium: '~2 hrs',
  high: 'Project',
};

const SOURCE_LABELS: Record<string, string> = {
  seo: 'SEO Audit',
  gap: 'Gap Analysis',
  content: 'Content',
  asset: 'Ready to use',
};

const PRIORITIES: ActionPriority[] = ['critical', 'high', 'medium', 'low'];
const FOCUS_AREAS: ActionFocusArea[] = [
  'technical',
  'on_page',
  'local',
  'off_page',
  'schema',
  'aeo',
  'conversion',
  'competitive',
];

// ─── Action row ───────────────────────────────────────────────────────────────

function ActionRow({ item }: { item: ActionItem }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!item.quickAction) return;
    await navigator.clipboard.writeText(item.quickAction.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.row}>
      <div
        className={styles.priorityDot}
        style={{ background: PRIORITY_COLORS[item.priority] }}
        title={PRIORITY_LABELS[item.priority]}
      />

      <div className={styles.rowBody}>
        <div className={styles.rowTitle}>{item.title}</div>
        <div className={styles.rowDesc}>{item.description}</div>
      </div>

      <div className={styles.rowMeta}>
        <span className={styles.tag}>{FOCUS_AREA_LABELS[item.focusArea]}</span>
        <span className={styles.tag}>{EFFORT_LABELS[item.effort] ?? item.effort}</span>
        <span className={`${styles.tag} ${styles.tagSource}`}>
          {SOURCE_LABELS[item.source] ?? item.source}
        </span>
      </div>

      {item.quickAction && (
        <button className={styles.copyBtn} onClick={handleCopy}>
          {copied ? (
            <span className={styles.iconText}>
              <Check size={14} aria-hidden /> Copied
            </span>
          ) : (
            item.quickAction.label
          )}
        </button>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ActionQueuePage() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';

  const [priorityFilter, setPriorityFilter] = useState<ActionPriority | 'all'>('all');
  const [focusFilter, setFocusFilter] = useState<ActionFocusArea | 'all'>('all');

  const query = useQuery({
    queryKey: dashboardKeys.actions(locationId),
    queryFn: () => actionsApi.getItems(locationId),
    enabled: !!locationId,
  });

  if (!currentLocation) {
    return (
      <EmptyState
        icon={<MapPin size={40} strokeWidth={1.5} aria-hidden />}
        title="No location selected"
        description="Please select a location to view your action queue."
      />
    );
  }

  const items = query.data?.data ?? [];
  const filtered = items.filter((item) => {
    if (priorityFilter !== 'all' && item.priority !== priorityFilter) return false;
    if (focusFilter !== 'all' && item.focusArea !== focusFilter) return false;
    return true;
  });

  const countByPriority = (p: ActionPriority) => items.filter((i) => i.priority === p).length;
  const countByFocus = (f: ActionFocusArea) => items.filter((i) => i.focusArea === f).length;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Action Queue"
        subtitle={currentLocation.name}
        actions={
          items.length > 0 ? (
            <span className={styles.totalBadge}>{items.length} items</span>
          ) : undefined
        }
      />

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Priority</span>
          <div className={styles.filterChips}>
            <button
              className={`${styles.chip} ${priorityFilter === 'all' ? styles.chipActive : ''}`}
              onClick={() => setPriorityFilter('all')}
            >
              All
            </button>
            {PRIORITIES.filter((p) => countByPriority(p) > 0).map((p) => (
              <button
                key={p}
                className={`${styles.chip} ${priorityFilter === p ? styles.chipActive : ''}`}
                onClick={() => setPriorityFilter(p)}
                style={
                  priorityFilter === p
                    ? { borderColor: PRIORITY_COLORS[p], color: PRIORITY_COLORS[p] }
                    : {}
                }
              >
                <span className={styles.chipDot} style={{ background: PRIORITY_COLORS[p] }} />
                {PRIORITY_LABELS[p]}
                <span className={styles.chipCount}>{countByPriority(p)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Focus Area</span>
          <div className={styles.filterChips}>
            <button
              className={`${styles.chip} ${focusFilter === 'all' ? styles.chipActive : ''}`}
              onClick={() => setFocusFilter('all')}
            >
              All
            </button>
            {FOCUS_AREAS.filter((f) => countByFocus(f) > 0).map((f) => (
              <button
                key={f}
                className={`${styles.chip} ${focusFilter === f ? styles.chipActive : ''}`}
                onClick={() => setFocusFilter(f)}
              >
                {FOCUS_AREA_LABELS[f]}
                <span className={styles.chipCount}>{countByFocus(f)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      {query.isLoading ? (
        <div className={styles.list}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 size={40} strokeWidth={1.5} aria-hidden />}
          title="No action items"
          description="Complete the onboarding pipeline to generate your first recommendations."
        />
      ) : filtered.length === 0 ? (
        <p className={styles.emptyFilter}>No items match the selected filters.</p>
      ) : (
        <div className={styles.list}>
          {filtered.map((item) => (
            <ActionRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
